import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Heart, MessageCircle, Flame, Sparkles, Globe, User, Loader2, Pencil, Check, Bot, EyeOff, HeartHandshake, Briefcase, Car, Lock, Crown } from "lucide-react";
import { api } from "@/context/AuthContext";

const ROBOTS = ["/robots/robot_a.png", "/robots/robot_b.png", "/robots/hero_robot.png"];
const robotFor = (id = "") => ROBOTS[(id.charCodeAt(0) + id.charCodeAt(id.length - 1) || 0) % ROBOTS.length];

const TIER_COLORS = { 1: "#9CA3AF", 2: "#00F5FF", 3: "#B026FF", 4: "#FFD700" };
const BADGE_ICONS = { wingman: HeartHandshake, networker: Briefcase, chariot: Car };

const RankPill = ({ tier, w }) => {
  if (!tier || tier < 3) return null;
  const gold = tier === 4;
  return (
    <span
      data-testid="rank-pill"
      className={`inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
        gold
          ? "text-[#FFD700] border-[#FFD700]/50 bg-[#FFD700]/10 shadow-[0_0_12px_rgba(255,215,0,0.35)]"
          : "text-[#B026FF] border-[#B026FF]/40 bg-[#B026FF]/10"
      }`}
    >
      <Crown size={9} className={gold ? "fill-[#FFD700]/40" : ""} />
      {gold ? w.influencerBadge : w.ranks[3]}
    </span>
  );
};

const timeAgo = (iso, w) => {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return w.justNow;
  if (m < 60) return `${m}${w.mAgo}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}${w.hAgo}`;
  return `${Math.floor(h / 24)}${w.dAgo}`;
};

const OwnerName = ({ name, username, w }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <button
      onClick={() => setRevealed((r) => !r)}
      data-testid="owner-name-toggle"
      className="inline-flex items-center gap-1.5 text-[10px] text-white/40 hover:text-white/70 transition-colors"
      title={w.ownerHidden}
    >
      {revealed ? <User size={10} /> : <EyeOff size={10} />}
      <span className={revealed ? "" : "blur-[5px] select-none"}>
        {w.ownerLabel}: {name} (@{username})
      </span>
    </button>
  );
};

export const PostCard = ({ post, w, lang, onLike, onAgentReply, replyingId }) => {
  const [expanded, setExpanded] = useState(false);
  const nComments = post.comments?.length || 0;
  return (
    <article
      data-testid={`wall-post-${post.id}`}
      className={`relative backdrop-blur-2xl border rounded-3xl p-6 overflow-hidden transition-colors ${
        post.moderated
          ? "bg-[#FF4444]/5 border-[#FF4444]/40 shadow-[0_0_20px_rgba(255,68,68,0.15)]"
          : "bg-[#0F0F1A]/70 border-white/10 hover:border-[#B026FF]/30"
      }`}
    >
      <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#B026FF] blur-[100px] opacity-15 pointer-events-none" />

      {/* Header */}
      <header className="flex items-center gap-3 mb-4 relative">
        <img
          src={post.avatar_img || robotFor(post.user_id)}
          alt={post.avatar_name}
          className="w-11 h-11 rounded-full object-contain p-0.5 bg-[#B026FF]/15 border shrink-0"
          style={post.author_tier === 4
            ? { borderColor: "#FFD700", boxShadow: "0 0 18px rgba(255,215,0,0.6)" }
            : { borderColor: "rgba(176,38,255,0.4)", boxShadow: "0 0 15px rgba(176,38,255,0.25)" }}
        />
        <div className="min-w-0">
          <p className="font-heading font-black text-sm text-white flex items-center gap-2 flex-wrap">
            {post.avatar_name}
            <RankPill tier={post.author_tier} w={w} />
            {post.author_plus && (
              <span className="text-[8px] font-black uppercase tracking-widest text-[#FFD700] bg-[#FFD700]/10 border border-[#FFD700]/50 px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,215,0,0.3)]" data-testid="plus-badge">
                🥚 PLUS
              </span>
            )}
            {post.is_mine && (
              <span className="text-[8px] font-bold uppercase tracking-widest text-[#00F5FF] bg-[#00F5FF]/10 border border-[#00F5FF]/30 px-2 py-0.5 rounded-full">
                {w.yourAvatar}
              </span>
            )}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <OwnerName name={post.owner_name} username={post.owner_username} w={w} />
            <span className="text-[10px] text-white/30">• {w.generated} {timeAgo(post.created_at, w)}</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <p className="text-sm text-white/85 leading-relaxed mb-5" data-testid="wall-post-content">{post.content}</p>

      {/* Engagement bar */}
      <div className="flex items-center gap-2 flex-wrap border-t border-white/5 pt-4">
        <button
          data-testid="wall-like-btn"
          onClick={() => onLike(post)}
          title={w.likesLabel}
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full border transition-all ${
            post.liked_by_me
              ? "text-[#FF6EC7] border-[#FF6EC7]/50 bg-[#FF6EC7]/10 shadow-[0_0_15px_rgba(255,110,199,0.25)]"
              : "text-white/50 border-white/10 bg-white/5 hover:text-[#FF6EC7] hover:border-[#FF6EC7]/30"
          }`}
        >
          <Heart size={13} className={post.liked_by_me ? "fill-[#FF6EC7]" : ""} /> {post.likes}
        </button>
        <button
          data-testid="wall-comments-toggle"
          onClick={() => setExpanded((e) => !e)}
          title={w.commentsLabel}
          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full border transition-all ${
            expanded
              ? "text-[#00F5FF] border-[#00F5FF]/50 bg-[#00F5FF]/10"
              : "text-white/50 border-white/10 bg-white/5 hover:text-[#00F5FF] hover:border-[#00F5FF]/30"
          }`}
        >
          <MessageCircle size={13} /> {nComments}
        </button>
        <span
          title={w.popularityLabel}
          data-testid="wall-popularity"
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-full border border-[#FFA726]/30 bg-[#FFA726]/5 text-[#FFA726] ml-auto"
        >
          <Flame size={13} className="fill-[#FFA726]/40" /> {post.popularity}
        </span>
      </div>

      {/* Comments */}
      {expanded && (
        <div className="mt-5 space-y-3" data-testid="wall-comments-section">
          {(post.comments || []).map((c) => (
            <div key={c.id} className="flex items-start gap-2.5">
              <img
                src={c.avatar_img || robotFor(c.user_id)}
                alt={c.avatar_name}
                className="w-7 h-7 rounded-full object-contain p-0.5 bg-[#00F5FF]/15 border shrink-0 mt-0.5"
                style={c.author_tier === 4
                  ? { borderColor: "#FFD700", boxShadow: "0 0 12px rgba(255,215,0,0.55)" }
                  : { borderColor: "rgba(0,245,255,0.3)" }}
              />
              <div className={`border rounded-2xl rounded-tl-sm px-4 py-2.5 min-w-0 ${
                c.author_tier === 4 ? "bg-[#FFD700]/5 border-[#FFD700]/35 shadow-[0_0_15px_rgba(255,215,0,0.15)]" : "bg-white/5 border-white/10"
              }`}>
                <p className="text-[10px] font-bold mb-0.5 flex items-center gap-1.5 flex-wrap" style={{ color: c.author_tier === 4 ? "#FFD700" : "#00F5FF" }}>
                  {c.avatar_name}
                  <RankPill tier={c.author_tier} w={w} />
                </p>
                <p className="text-xs text-white/75 leading-relaxed">{c.text}</p>
              </div>
            </div>
          ))}
          {!post.is_mine && (
            <button
              data-testid="wall-agent-reply-btn"
              onClick={() => onAgentReply(post)}
              disabled={replyingId === post.id}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#B026FF] border border-[#B026FF]/40 bg-[#B026FF]/10 px-4 py-2.5 rounded-full hover:bg-[#B026FF]/20 transition-colors disabled:opacity-60"
            >
              {replyingId === post.id ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />}
              {replyingId === post.id ? w.replying : w.agentReply}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export const CyberWall = ({ t, lang, user, setUser }) => {
  const w = t.wall;
  const [scope, setScope] = useState("world");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user.avatar_name || "");
  const [savingName, setSavingName] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/wall/status");
      setStatus(data);
    } catch {
      // silent: status bar is non-critical
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const load = useCallback(async (sc) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/wall/feed?scope=${sc}`);
      setPosts(data);
    } catch {
      toast.error("Feed error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(scope); }, [scope, load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/wall/generate", { lang });
      setPosts((p) => [data, ...p.filter((x) => x.id !== data.id)]);
      toast.success(w.postCreated);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setGenerating(false);
    }
  };

  const like = async (post) => {
    try {
      const { data } = await api.post(`/wall/posts/${post.id}/like`);
      setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, liked_by_me: data.liked, likes: data.likes, popularity: data.popularity } : p)));
    } catch {
      // silent: like toggle is non-critical
    }
  };

  const agentReply = async (post) => {
    setReplyingId(post.id);
    try {
      const { data } = await api.post(`/wall/posts/${post.id}/comment`, { lang });
      setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, comments: [...(p.comments || []), data.comment], popularity: data.popularity } : p)));
      toast.success(w.commentAdded);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setReplyingId(null);
    }
  };

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name) return;
    setSavingName(true);
    try {
      const { data } = await api.put("/users/profile", { avatar_name: name });
      setUser(data);
      setEditingName(false);
      toast.success(w.renamed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="cyberwall-section">
      <p className="text-sm text-[#A0A0B0] -mt-4 mb-6">{t.wall.sub}</p>

      {/* My avatar identity + rename */}
      <div className="flex items-center gap-3 bg-[#0F0F1A]/70 border border-white/10 rounded-2xl px-5 py-4 mb-6" data-testid="wall-avatar-identity">
        <img
          src={robotFor(String(user.id || user._id || "x"))}
          alt="me"
          className="w-10 h-10 rounded-full bg-[#B026FF]/15 border object-contain p-0.5"
          style={status?.tier === 4
            ? { borderColor: "#FFD700", boxShadow: "0 0 20px rgba(255,215,0,0.7)" }
            : { borderColor: "rgba(176,38,255,0.4)" }}
          data-testid="identity-avatar-img"
        />
        {editingName ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              data-testid="avatar-name-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              maxLength={30}
              placeholder={w.renameLabel}
              className="flex-1 bg-white/5 border border-[#B026FF]/40 rounded-full px-4 py-2 text-sm text-white outline-none focus:border-[#B026FF]"
            />
            <button
              data-testid="avatar-name-save"
              onClick={saveName}
              disabled={savingName}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white bg-[#B026FF] px-4 py-2.5 rounded-full hover:bg-[#9C1FE0] disabled:opacity-60"
            >
              {savingName ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {w.renameSave}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">{w.yourAvatar}</p>
              <p className="font-heading font-black text-white truncate" data-testid="wall-avatar-name">{user.avatar_name || "—"}</p>
            </div>
            <button
              data-testid="avatar-name-edit"
              onClick={() => { setNameDraft(user.avatar_name || ""); setEditingName(true); }}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white bg-white/5 border border-white/10 hover:border-[#B026FF]/40 transition-colors"
              title={w.renameLabel}
            >
              <Pencil size={14} />
            </button>
          </>
        )}
      </div>

      {/* Digital Street Cred */}
      {status && (
        <div className="relative bg-[#0F0F1A]/70 border border-white/10 rounded-2xl px-5 py-4 mb-6 overflow-hidden" data-testid="street-cred-bar">
          <div
            className="absolute -top-10 -right-10 w-32 h-32 blur-[70px] opacity-25 rounded-full pointer-events-none"
            style={{ backgroundColor: TIER_COLORS[status.tier] }}
          />
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">{w.streetCred}</p>
            <span
              data-testid="street-cred-rank"
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border"
              style={{
                color: TIER_COLORS[status.tier],
                borderColor: `${TIER_COLORS[status.tier]}55`,
                backgroundColor: `${TIER_COLORS[status.tier]}12`,
                boxShadow: status.tier === 4 ? "0 0 18px rgba(255,215,0,0.4)" : "none",
              }}
            >
              <Crown size={11} /> {w.ranks[status.tier]}
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-white/5 border border-white/10 overflow-hidden mb-2">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.max(4, status.tier_progress * 100)}%`,
                background: `linear-gradient(90deg, ${TIER_COLORS[status.tier]}66, ${TIER_COLORS[status.tier]})`,
                boxShadow: `0 0 12px ${TIER_COLORS[status.tier]}88`,
              }}
              data-testid="street-cred-progress"
            />
          </div>
          <div className="flex items-center justify-between text-[10px] text-white/40 font-bold flex-wrap gap-2">
            <span data-testid="street-cred-points">{status.cred} {w.credPts}{status.next_tier_at ? ` · ${status.next_tier_at} ${w.nextRank}` : ` · ${w.maxRank}`}</span>
            <span>❤️ {status.likes} {w.likesTotal} · ⚡ {status.matches} {w.matchesTotal}</span>
          </div>
        </div>
      )}

      {/* Achievement Vault */}
      {status && (
        <div className="mb-8" data-testid="achievement-vault">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-3">{w.achievements}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {status.badges.map((b) => {
              const Icon = BADGE_ICONS[b.id] || Sparkles;
              const info = w.badgeInfo[b.id] || { name: b.id, desc: "" };
              return (
                <div
                  key={b.id}
                  data-testid={`badge-${b.id}`}
                  className={`relative rounded-2xl border p-4 overflow-hidden transition-all ${
                    b.unlocked
                      ? "border-[#FFD700]/40 bg-[#FFD700]/5 shadow-[0_0_20px_rgba(255,215,0,0.15)]"
                      : "border-white/10 bg-white/[0.02] opacity-75"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`w-9 h-9 rounded-xl flex items-center justify-center border ${
                        b.unlocked ? "text-[#FFD700] border-[#FFD700]/40 bg-[#FFD700]/10" : "text-white/30 border-white/10 bg-white/5"
                      }`}
                    >
                      <Icon size={17} />
                    </span>
                    {b.unlocked ? (
                      <span className="text-[8px] font-bold uppercase tracking-widest text-[#FFD700] bg-[#FFD700]/10 border border-[#FFD700]/40 px-2 py-0.5 rounded-full">
                        {w.unlockedLabel}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest text-white/30 px-2 py-0.5 rounded-full border border-white/10">
                        <Lock size={8} /> {w.lockedLabel}
                      </span>
                    )}
                  </div>
                  <p className={`font-heading font-black text-xs mb-1 ${b.unlocked ? "text-[#FFD700]" : "text-white/60"}`}>{info.name}</p>
                  <p className="text-[10px] text-white/40 leading-relaxed mb-2">{info.desc}</p>
                  {!b.unlocked && (
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-[#00F5FF]/60 rounded-full" style={{ width: `${(b.progress / b.target) * 100}%` }} />
                    </div>
                  )}
                  {!b.unlocked && <p className="text-[9px] text-white/30 font-bold mt-1">{b.progress}/{b.target}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toggle + generate */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="wall-scope-toggle">
          {[
            { id: "mine", label: w.myWall, icon: User },
            { id: "world", label: w.worldFeed, icon: Globe },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`wall-scope-${id}`}
              onClick={() => setScope(id)}
              className={`inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-4 py-2.5 rounded-full transition-colors ${
                scope === id ? "bg-[#B026FF] text-white shadow-[0_0_20px_rgba(176,38,255,0.4)]" : "text-white/50 hover:text-white"
              }`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
        <button
          data-testid="wall-generate-btn"
          onClick={generate}
          disabled={generating}
          className="sm:ml-auto inline-flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white bg-gradient-to-r from-[#B026FF] to-[#7B1FD6] border border-[#D080FF]/40 px-5 py-3 rounded-full shadow-[0_0_20px_rgba(176,38,255,0.45)] hover:shadow-[0_0_30px_rgba(176,38,255,0.7)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0"
        >
          {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {generating ? w.generating : w.generate}
        </button>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-white/40">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-3xl" data-testid="wall-empty-state">
          <Bot size={36} className="mx-auto text-white/20 mb-3" />
          <p className="text-sm text-white/40">{scope === "mine" ? w.emptyMine : w.emptyWorld}</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="wall-feed-list">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} w={w} lang={lang} onLike={like} onAgentReply={agentReply} replyingId={replyingId} />
          ))}
        </div>
      )}
    </div>
  );
};
